import { ErrorResolutionActions } from '../../app/common/errorResolution/errorResolutionActions';
import ErrorRedirectService from '../../routes/error/errorRedirect.service';
import { NotificationsActions } from '../../app/common/notifications/notificationsActions';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import { privacyCenterActionTypes } from './privacyCenter.constants';
import {
    deleteAccount,
    getServiceRecommendations,
    getServicesAndConsentsStatus,
    getServicesMapping,
    updateOptionalConsentStatus,
    unlinkSocialProvider as unlinkSocialProviderService,
} from './privacyCenter.services';
import { ErrorResolutionTypes } from '../../app/common/errorResolution/errorResolution';
import { NOTIFICATIONS } from '../../app/common/constants/styles';
import { NotificationType } from '../../app/common/notifications/notificationType.enum';
import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import { createDsrDeleteRequest } from './dsr/dsr.services';
import { SocialAccountRatEvents } from '../socialAccounts/utils/socialAccountRatEvents';

export const privacyCenterActions = {
    updateConsentStatus,
    startDeleteAccount,
    cancelMembership,
    loadServicesConfig,
    loadServicesAndConsentsStatus,
    loadRecommendedEUServices,
    submitDsrRequest,
    openDSRForm,
    closeDSRForm,
    toggleDSRDeleteStatusInfo,
    unlinkSocialProvider,
    openSocialUnlinkForm,
    closeSocialUnlinkForm,
};

function loadRecommendedEUServices() {
    return async (dispatch) => {
        dispatch(request());
        getServiceRecommendations(webStorage.getActiveClientId())
            .then((recommendations) => {
                dispatch(success(recommendations));
            })
            .catch((error) => {
                dispatch(ErrorResolutionActions.processErrorResolution(error));
                dispatch(failure(error));
            });
    };

    function request() {
        return { type: privacyCenterActionTypes.LOAD_SERVICES_RECOMMENDATION_REQUEST };
    }

    function success(recommendations) {
        return {
            type: privacyCenterActionTypes.LOAD_SERVICES_RECOMMENDATION_SUCCESS,
            recommendations,
        };
    }

    function failure(error) {
        return { type: privacyCenterActionTypes.LOAD_SERVICES_RECOMMENDATION_FAILURE, error };
    }
}

function loadServicesConfig() {
    return async (dispatch) => {
        dispatch(request());
        getServicesMapping()
            .then((servicesConfig) => {
                dispatch(success(servicesConfig));
            })
            .catch((error) => {
                dispatch(ErrorResolutionActions.processErrorResolution(error));
                dispatch(failure(error));
            });
    };

    function request() {
        return { type: privacyCenterActionTypes.LOAD_SERVICES_CONFIG_REQUEST };
    }

    function success(servicesConfig) {
        return {
            type: privacyCenterActionTypes.LOAD_SERVICES_CONFIG_SUCCESS,
            servicesConfig,
        };
    }

    function failure(error) {
        return { type: privacyCenterActionTypes.LOAD_SERVICES_CONFIG_FAILURE, error };
    }
}

function startDeleteAccount() {
    return (dispatch, getState) => {
        const { clientConfig } = getState().app;
        dispatch(deleteAccountClick(clientConfig));
    };

    function deleteAccountClick(clientConfig) {
        return {
            type: privacyCenterActionTypes.CANCEL_MEMBERSHIP_START,
            clientConfig,
            meta: {
                rat: {},
            },
        };
    }
}

function cancelMembership(user, password) {
    return (dispatch, getState) => {
        const data = {
            clientId: getState().app.clientConfig.clientId,
            currentPassword: password,
        };

        dispatch(request(user));
        deleteAccount(data, password)
            .then(() => {
                const clientConfig = getState().app.clientConfig;

                dispatch(
                    ErrorResolutionActions.processErrorResolution({
                        errorMessageId: 'notifications.account.deleted',
                        type: ErrorResolutionTypes.ERROR_CODE_MESSAGE,
                    }),
                );

                setInterval(() => {
                    ErrorRedirectService.takeToLogout();
                    dispatch(success(clientConfig));
                }, NOTIFICATIONS[NotificationType.Error].expiry);
            })
            .catch((error) => {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        error,
                        'notifications.account.password.update.failure',
                    ),
                );
                dispatch(failure(error));
            });
    };

    function request(member) {
        return { type: privacyCenterActionTypes.CANCEL_MEMBERSHIP_REQUEST, member };
    }

    function success(clientConfig) {
        return {
            type: privacyCenterActionTypes.CANCEL_MEMBERSHIP_SUCCESS,
            clientConfig,
            meta: {
                rat: {},
            },
        };
    }

    function failure(error) {
        return { type: privacyCenterActionTypes.CANCEL_MEMBERSHIP_FAILURE, error };
    }
}

function loadServicesAndConsentsStatus(lang) {
    return (dispatch) => {
        dispatch(request());

        getServicesAndConsentsStatus(lang)
            .then((servicesAndConsentsStatus) => {
                dispatch(success(servicesAndConsentsStatus));
            })
            .catch((error) => {
                dispatch(ErrorResolutionActions.processErrorResolution(error));
                dispatch(failure(error));
            });
    };

    function request() {
        return { type: privacyCenterActionTypes.GET_SERVICES_AND_CONSENTS_REQUEST };
    }

    function success(servicesAndConsentsStatus) {
        return {
            type: privacyCenterActionTypes.GET_SERVICES_AND_CONSENTS_SUCCESS,
            servicesAndConsentsStatus,
        };
    }

    function failure(error) {
        return { type: privacyCenterActionTypes.GET_SERVICES_AND_CONSENTS_FAILURE, error };
    }
}

function updateConsentStatus(data, locale) {
    return (dispatch, getState) => {
        const { serviceUuid } = data;
        dispatch(request(data));
        updateOptionalConsentStatus(data, locale)
            .then((services) => {
                dispatch(success({ services, serviceUuid }, getState().app.clientConfig, data));
                dispatch(
                    NotificationsActions.showSuccess(
                        TranslationHelpers.getFormattedMessage(
                            'notifications.account.subscription.update.success',
                        ),
                    ),
                );
            })
            .catch((error) => {
                dispatch(ErrorResolutionActions.processErrorResolution(error));
                dispatch(failure(serviceUuid, error, data));
            });
    };

    function request(data) {
        return {
            type: privacyCenterActionTypes.UPDATE_CONSENT_REQUEST,
            data,
            meta: createRatMetaData(data),
        };
    }

    function success(response, clientConfig, data) {
        return {
            type: privacyCenterActionTypes.UPDATE_CONSENT_SUCCESS,
            response,
            clientConfig,
            meta: createRatMetaData(data),
        };
    }

    function failure(serviceUuid, error, data) {
        return {
            type: privacyCenterActionTypes.UPDATE_CONSENT_FAILURE,
            payload: { serviceUuid, error },
            meta: createRatMetaData(data),
        };
    }
}

function createRatMetaData(requestData) {
    const NEWSLETTER_SUBSCRIBED = 'newsletter_subscribed';
    const NEWSLETTER_UNSUBSCRIBED = 'newsletter_unsubscribed';
    const CONSENT_ACCEPTED = 'consent_accepted';
    const CONSENT_REJECTED = 'consent_rejected';
    const ACCEPTED = 'accepted';

    const ratMetaData = {
        rat: {},
    };

    if (requestData.extraParameters.hasOwnProperty('nlSubscription')) {
        ratMetaData.rat.cvData = {
            mailmag_subscribe: requestData.extraParameters.nlSubscription
                ? NEWSLETTER_SUBSCRIBED
                : NEWSLETTER_UNSUBSCRIBED,
        };
    }
    if (requestData.extraParameters.hasOwnProperty('reaction')) {
        ratMetaData.rat.cvData = {
            mailmag_subscribe:
                requestData.extraParameters.reaction === ACCEPTED
                    ? CONSENT_ACCEPTED
                    : CONSENT_REJECTED,
        };
    }

    return ratMetaData;
}

function submitDsrRequest(body, password) {
    return (dispatch, getState) => {
        dispatch({ type: privacyCenterActionTypes.DELETE_SERVICE_BTN_REQUEST });
        createDsrDeleteRequest(body, password)
            .then((response) => {
                /* When RID is down/unreachable (any 5xx error from their side) then,
                 * in this case backend is sending 200 OK with error details response and success param as 'false'
                 **/
                if (response.success === false) {
                    return Promise.reject({
                        errorCode: response?.errorResponse?.code || 'myr-6999',
                        type: ErrorResolutionTypes.WARNING_CODE_MESSAGE,
                    });
                }
                dispatch(
                    NotificationsActions.showSuccess(
                        TranslationHelpers.getFormattedMessage('notifications.dsr.update.success'),
                    ),
                );
                dispatch({
                    type: privacyCenterActionTypes.DELETE_SERVICE_BTN_SUCCESS,
                    data: response,
                });
                // Redirect to parent site via logout flow if the requested delete service is same as logged-in service
                const clientServiceUuid = getState().app.clientConfig.serviceUuid;
                if (clientServiceUuid === response.serviceUuid) {
                    const redirectUrl = getState().app.clientConfig.returnUrl;
                    setTimeout(() => {
                        dispatch(
                            NotificationsActions.showRedirectWarning(
                                redirectUrl,
                                'notifications.redirect.reason.expired',
                            ),
                        );
                        setTimeout(() => {
                            ErrorRedirectService.takeToLogout(redirectUrl);
                        }, 3000);
                    }, 1000);
                }
            })
            .catch((error) => {
                dispatch({ type: privacyCenterActionTypes.DELETE_SERVICE_BTN_FAILED });
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        error,
                        'notifications.dsr.update.failure',
                    ),
                );
            });
    };
}

function openDSRForm(serviceUuid) {
    return {
        type: privacyCenterActionTypes.OPEN_DSR_FORM,
        serviceUuid,
    };
}

function closeDSRForm() {
    return {
        type: privacyCenterActionTypes.CLOSE_DSR_FORM,
    };
}

function toggleDSRDeleteStatusInfo(serviceUuid, value) {
    return {
        type: privacyCenterActionTypes.TOGGLE_DSR_DELETE_STATUS_INFO,
        value,
        serviceUuid,
    };
}

function unlinkSocialProvider(providerId, providerName, unlinkTargetId) {
    return (dispatch) => {
        dispatch(request(providerId));
        SocialAccountRatEvents.trackUnlinkConfirm(providerName, unlinkTargetId);
        unlinkSocialProviderService(providerId)
            .then((response) => {
                if (response.success === false) {
                    return Promise.reject({
                        errorCode: response?.errorResponse?.code || 'social-unlink-failed',
                        type: ErrorResolutionTypes.WARNING_CODE_MESSAGE,
                    });
                }
                SocialAccountRatEvents.trackUnlinkSuccessful(providerName, unlinkTargetId);
                dispatch(
                    NotificationsActions.showSuccess(
                        TranslationHelpers.getFormattedMessage(
                            'notifications.social.unlink.success',
                            { provider: providerName },
                        ),
                    ),
                );
                dispatch(success(response));
                dispatch(closeSocialUnlinkForm());
                // Reload services config to get updated social accounts
                dispatch(loadServicesConfig());
            })
            .catch((error) => {
                SocialAccountRatEvents.trackUnlinkFailed(providerName, unlinkTargetId);
                // Just display the warning notification
                dispatch(
                    NotificationsActions.showWarning(
                        TranslationHelpers.getFormattedMessage(
                            'notifications.social.unlink.failure',
                        ),
                    ),
                );
                dispatch(failure(providerId, error));
            });
    };

    function request(providerId) {
        return {
            type: privacyCenterActionTypes.UNLINK_SOCIAL_PROVIDER_REQUEST,
            providerId,
        };
    }

    function success(response) {
        return {
            type: privacyCenterActionTypes.UNLINK_SOCIAL_PROVIDER_SUCCESS,
            response,
        };
    }

    function failure(providerId, error) {
        return {
            type: privacyCenterActionTypes.UNLINK_SOCIAL_PROVIDER_FAILURE,
            providerId,
            error,
        };
    }
}

function openSocialUnlinkForm(providerId, providerName, unlinkTargetId) {
    SocialAccountRatEvents.trackUnlinkClick(providerName, unlinkTargetId);
    return {
        type: privacyCenterActionTypes.OPEN_SOCIAL_UNLINK_FORM,
        providerId,
        providerName,
        unlinkTargetId,
    };
}

function closeSocialUnlinkForm() {
    return {
        type: privacyCenterActionTypes.CLOSE_SOCIAL_UNLINK_FORM,
    };
}
